import {
  TELEMETRY_ID_ATTR,
  TELEMETRY_LABEL_ATTR,
  TELEMETRY_NAME_ATTR,
  TELEMETRY_SECTION_ATTR,
} from './clickTracking';
import { validateGa4Key } from './ga4Key';

/**
 * Input for {@link telemetryProps}. `name`, `section`, and `label` are required by
 * convention (S5); `id` is optional. Because these are required in the type, omitting
 * one is a TypeScript compile error at the call site - the convention is enforced before
 * lint or runtime.
 */
export interface TelemetryPropsInput {
  /** Stable, globally-unique GA4 key (maps to `data-telemetry-name`). */
  name: string;
  /** Logical grouping the element belongs to, e.g. `orders_view` (maps to `data-telemetry-section`). */
  section: string;
  /** Human-readable label for the interaction (maps to `data-telemetry-label`). */
  label: string;
  /** Optional dynamic identifier - order id, product id, etc. (maps to `data-telemetry-id`). */
  id?: string;
}

/**
 * The `data-telemetry-*` attribute bag returned by {@link telemetryProps}: the three
 * required keys plus an optional `data-telemetry-id`. Spread directly onto a JSX element.
 */
export type TelemetryAttributes = Record<
  typeof TELEMETRY_NAME_ATTR | typeof TELEMETRY_SECTION_ATTR | typeof TELEMETRY_LABEL_ATTR,
  string
> &
  Partial<Record<typeof TELEMETRY_ID_ATTR, string>>;

/**
 * Builds the `data-telemetry-*` attributes for a tracked interaction, enforcing the
 * naming + placement convention at compile time.
 *
 * The delegated click listener ({@link initClickTracking}) reads these attributes off the
 * nearest ancestor carrying `data-telemetry-name` and normalizes them into a single
 * `telemetry_click` dataLayer event, so no per-element wiring is required - just spread the
 * result onto the interactive element:
 *
 * ```tsx
 * <button {...telemetryProps({ name: 'orders_view_so_link', section: 'orders_view', label: 'View order' })}>
 *   View
 * </button>
 * ```
 *
 * `name` must be globally unique (it is the GA4 key); `section` and `label` are required so
 * GA4 events are consistently grouped and readable. `id` is emitted only when supplied.
 *
 * Prefer this helper over hand-writing `data-telemetry-*` attributes: the required fields
 * are guaranteed by the type, and the attribute-completeness ESLint rules
 * (`telemetry/name-required`, `telemetry/section-label-required`) skip any element that uses
 * a JSX spread - the attributes are not statically knowable, and this helper already proves
 * correctness at compile time.
 */
export function telemetryProps({ name, section, label, id }: TelemetryPropsInput): TelemetryAttributes {
  if (import.meta.env.DEV) {
    // The ESLint rule only sees static string literals; this backstops dynamic `name`
    // values that reach GA4 as an event parameter key.
    const reason = validateGa4Key(name);
    if (reason) {
      // eslint-disable-next-line no-console
      console.error(`telemetryProps: data-telemetry-name "${name}" is not a valid GA4 key: ${reason}.`);
    }
  }

  return {
    [TELEMETRY_NAME_ATTR]: name,
    [TELEMETRY_SECTION_ATTR]: section,
    [TELEMETRY_LABEL_ATTR]: label,
    ...(id !== undefined ? { [TELEMETRY_ID_ATTR]: id } : {}),
  };
}
